/**
 * resolveBoxTiming — canonical timing resolution
 *
 * Shared by the editor timeline (buildLayers.js) and the visor (VisorCanvasSli + VisorBox).
 * Both sides must use this function so they produce identical startFrame/endFrame values.
 *
 * Priority order for startFrame:
 *   1. userMovedStart  — timeline.startFrame > 0 (user dragged the layer bar)
 *   2. delay-as-entry  — hasNewAnim && delay > 0 && !userMovedStart → startFrame = delay * FPS
 *   3. explicit zero   — timeline.startFrame === 0
 *   4. legacy keyframes — animationsById opacity keyframes (old scenejs system)
 *   5. undefined       — no gate; box is always visible
 *
 * animDelay is always 0 because startFrame already encodes the absolute entry time.
 * Passing animDelay > 0 to framer-motion on top of a mount gate would double-count.
 */

const FPS_DEFAULT = 30;

export function resolveBoxTiming(box, legacyAnims = [], FPS = FPS_DEFAULT) {
  const timeline = box?.timeline || {};
  const boxAnim = box?.boxAnimation;
  const textAnim = box?.textAnimation;
  const hasNewAnim = !!(boxAnim?.animation || textAnim?.animation);
  const rawDelay = boxAnim?.delay ?? textAnim?.delay ?? null;

  // User explicitly dragged the layer bar on the timeline
  const userMovedStart = typeof timeline.startFrame === "number" && timeline.startFrame > 0;

  let startFrame;
  let endFrame;

  // ── startFrame ──────────────────────────────────────────────────────
  if (userMovedStart) {
    startFrame = timeline.startFrame;
  } else if (hasNewAnim && typeof rawDelay === "number" && rawDelay > 0) {
    startFrame = Math.round(rawDelay * FPS);
  } else if (typeof timeline.startFrame === "number") {
    startFrame = timeline.startFrame; // explicit 0
  } else if (!hasNewAnim && legacyAnims.length > 0) {
    const sorted = [...legacyAnims].sort(
      (a, b) =>
        (typeof a.duration === "number" ? a.duration : 0) -
        (typeof b.duration === "number" ? b.duration : 0)
    );
    const firstVisible = sorted.find((a) => typeof a.opacity === "number" && a.opacity > 0);
    if (firstVisible) {
      startFrame =
        firstVisible === sorted[0]
          ? 0
          : typeof firstVisible.duration === "number"
            ? Math.round(firstVisible.duration * FPS)
            : undefined;
    }
  }
  // else: undefined → no visibility gate

  // ── endFrame ─────────────────────────────────────────────────────────
  // Priority:
  //   1. userSetEndFrame flag — user explicitly dragged the right handle
  //   2. userMovedStart — user moved the layer bar (start > 0), so endFrame is also user-set
  //   3. no animation — endFrame is always respected
  //   4. animated + startFrame=0 — endFrame ignored (may be stale default from before anim was added)
  if (timeline.userSetEndFrame && typeof timeline.endFrame === "number") {
    endFrame = timeline.endFrame;
  } else if (userMovedStart && typeof timeline.endFrame === "number") {
    endFrame = timeline.endFrame;
  } else if (typeof timeline.endFrame === "number") {
    // Animasyon olsa da olmasa da timeline.endFrame varsa dikkate al.
    // Sadece timeline.endFrame yoksa (undefined) sceneMaxEndFrame'e bırak.
    endFrame = timeline.endFrame;
  }

  // Legacy: derive from last opacity=0 keyframe (only when no explicit endFrame)
  if (endFrame === undefined && !hasNewAnim && legacyAnims.length > 0) {
    const sorted = [...legacyAnims].sort(
      (a, b) =>
        (typeof a.duration === "number" ? a.duration : 0) -
        (typeof b.duration === "number" ? b.duration : 0)
    );
    const lastHide = [...sorted]
      .reverse()
      .find((a) => typeof a.opacity === "number" && a.opacity === 0);
    if (lastHide && typeof lastHide.duration === "number") {
      endFrame = Math.round(lastHide.duration * FPS);
    }
  }
  // else: undefined → visible to scene end

  // animDelay is always 0: startFrame already encodes the absolute entry time.
  // Do NOT pass boxAnimation.delay to framer-motion when startFrame is set —
  // that would cause the box to mount at startFrame AND then wait delay more seconds.
  return { startFrame, endFrame, animDelay: 0 };
}

/**
 * resolveBoxEndFrame — the frame a box leaves the stage (its own end, no scene cap).
 * Shared by editor buildLayers, visor mount gate and visor exit detection.
 *   1. Explicit timeline.endFrame wins.
 *   2. Else derived from media length: playStart + (trimEnd − trimStart).
 *      Caller supplies mediaDurationFrames (editor: getMediaDuration; visor: <video>.duration).
 *   3. Neither → null (caller picks the fallback, e.g. scene end).
 * Scene-end capping is deliberately left to callers (buildLayers needs the raw end).
 */
export function resolveBoxEndFrame(
  box,
  mediaDurationFrames = null,
  legacyAnims = [],
  FPS = FPS_DEFAULT
) {
  const { startFrame, endFrame } = resolveBoxTiming(box, legacyAnims, FPS);
  if (typeof endFrame === "number") return endFrame;
  if (typeof mediaDurationFrames === "number" && mediaDurationFrames > 0) {
    const tl = box?.timeline || {};
    const trimStart = typeof tl.trimStart === "number" ? tl.trimStart : 0;
    const trimEnd = typeof tl.trimEnd === "number" ? tl.trimEnd : mediaDurationFrames;
    const playStart =
      typeof tl.playStartFrame === "number"
        ? tl.playStartFrame
        : typeof startFrame === "number"
          ? startFrame
          : 0;
    return playStart + Math.max(0, trimEnd - trimStart);
  }
  return null;
}
